import { ActionIcon, Tooltip } from "@mantine/core";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { useSubmitSimilarPartFeedback } from "#modules/feedback/view/useSubmitSimilarPartFeedback";
import { Icon } from "#shared/icons/Icon";
import { Group } from "#shared/view/components/Group";
import { Text } from "#shared/view/components/Text";
import { SimilarPartFeedbackRating } from "shared";

type Props = {
  sourcePartId: string | undefined;
  targetPartId: string;
  index: number;
};

export const SimilarPartFeedbackButtons = ({ sourcePartId, targetPartId, index }: Props) => {
  const { t } = useTranslation();
  const { similarPartFeedback, submitFeedback, isSubmitting } = useSubmitSimilarPartFeedback({
    sourcePartId,
    targetPartId,
  });

  const isSourceSameAsTarget = sourcePartId === targetPartId;

  if (!sourcePartId || isSourceSameAsTarget) {
    return null;
  }

  const handleClick = (rating: SimilarPartFeedbackRating) => (event: MouseEvent) => {
    event.stopPropagation();
    submitFeedback(rating, index);
  };

  return (
    <Group gap="xs">
      <Text fz="xs" c="dimmed">
        {t("feedback.similarPart.label")}
      </Text>
      <Group gap={4}>
        <Tooltip label={t("feedback.similarPart.relevant")}>
          <ActionIcon
            size="xs"
            aria-label={t("feedback.similarPart.relevant")}
            variant={similarPartFeedback?.rating === 1 ? "filled" : "subtle"}
            color={similarPartFeedback?.rating === 1 ? "green" : "gray"}
            onClick={handleClick(1)}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            <Icon name="like" size={12} />
          </ActionIcon>
        </Tooltip>

        <Tooltip label={t("feedback.similarPart.irrelevant")}>
          <ActionIcon
            size="xs"
            aria-label={t("feedback.similarPart.irrelevant")}
            variant={similarPartFeedback?.rating === -1 ? "filled" : "subtle"}
            color={similarPartFeedback?.rating === -1 ? "red" : "gray"}
            onClick={handleClick(-1)}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            <Icon name="dislike" size={12} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
};
